import { useMemo } from "react";
import PropTypes from "prop-types";
import { Dialog } from "primereact/dialog";
import { Field, useFormState } from "react-final-form";
import { useIntl } from "react-intl";
import Form from "../../common/form";
import { DropdownInput } from "../../common/fields";
import * as Validations from "../../common/form/validator";
import * as SavedReportConstants from "../../../constants/SavedReportConstants";
import { TIME_OF_DAY_OPTIONS, weekdayName } from "../../../helpers/ScheduleSummary";

// Monday first; values are .NET DayOfWeek numbers (Sunday = 0).
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => index + 1);
const DEFAULT_TIME_OF_DAY = 8 * 60;

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const useScheduleOptions = () => {
  const intl = useIntl();
  return useMemo(
    () => ({
      frequencies: SavedReportConstants.SCHEDULE_FREQUENCIES.map((item) => ({
        id: item.id,
        name: intl.formatMessage({ id: item.name }),
      })),
      weekdays: WEEKDAYS.map((day) => ({
        id: day,
        name: weekdayName(day, intl),
      })),
      daysOfMonth: DAYS_OF_MONTH.map((day) => ({ id: day, name: String(day) })),
      times: TIME_OF_DAY_OPTIONS,
      // Same list as the assignment forms' time zone picker.
      timezones: Intl.supportedValuesOf("timeZone").map((item) => ({
        id: item,
        name: item,
      })),
      formats: SavedReportConstants.REPORT_FILE_FORMATS.map((item) => ({
        id: item.id,
        name: intl.formatMessage({ id: item.name }),
      })),
    }),
    [intl]
  );
};

const optionOf = (options, id) => options.find((item) => item.id === id);

const toFormValues = (schedule, options) => {
  const timezone = schedule?.timezone || browserTimezone();
  return {
    frequency: optionOf(
      options.frequencies,
      schedule?.frequency ?? SavedReportConstants.SCHEDULE_FREQUENCY_WEEKLY
    ),
    dayOfWeek: optionOf(options.weekdays, schedule?.dayOfWeek ?? 1),
    dayOfMonth: optionOf(options.daysOfMonth, schedule?.dayOfMonth ?? 1),
    timeOfDay: optionOf(
      options.times,
      schedule?.timeOfDay ?? DEFAULT_TIME_OF_DAY
    ),
    timezone: { id: timezone, name: timezone },
    format: optionOf(
      options.formats,
      schedule?.format ?? SavedReportConstants.REPORT_FILE_FORMAT_EXCEL
    ),
  };
};

// Body of PUT saved-reports/{id}/schedule (ReportScheduleRequest).
export const toScheduleRequest = (values) => {
  const frequency = values.frequency.id;
  return {
    frequency,
    dayOfWeek:
      frequency === SavedReportConstants.SCHEDULE_FREQUENCY_WEEKLY
        ? values.dayOfWeek.id
        : null,
    dayOfMonth:
      frequency === SavedReportConstants.SCHEDULE_FREQUENCY_MONTHLY
        ? values.dayOfMonth.id
        : null,
    timeOfDay: values.timeOfDay.id,
    timezone: values.timezone.id,
    format: values.format.id,
  };
};

const required = Validations.composeValidators(Validations.required);

const ScheduleFields = ({ options }) => {
  const intl = useIntl();
  const { values } = useFormState();
  const frequency = values?.frequency?.id;
  const label = (id) => intl.formatMessage({ id });

  return (
    <div className="grid">
      <div className="col-12 md:col-6">
        <Field
          name="frequency"
          component={DropdownInput}
          label={label("schedule_frequency")}
          placeholder={label("schedule_frequency")}
          options={options.frequencies}
          required
          validate={required}
        />
      </div>
      {(frequency === SavedReportConstants.SCHEDULE_FREQUENCY_WEEKLY && (
        <div className="col-12 md:col-6">
          <Field
            name="dayOfWeek"
            component={DropdownInput}
            label={label("schedule_day_of_week")}
            placeholder={label("schedule_day_of_week")}
            options={options.weekdays}
            required
            validate={required}
          />
        </div>
      )) ||
        null}
      {(frequency === SavedReportConstants.SCHEDULE_FREQUENCY_MONTHLY && (
        <div className="col-12 md:col-6">
          <Field
            name="dayOfMonth"
            component={DropdownInput}
            label={label("schedule_day_of_month")}
            placeholder={label("schedule_day_of_month")}
            options={options.daysOfMonth}
            required
            validate={required}
          />
          <small>{label("schedule_day_of_month_hint")}</small>
        </div>
      )) ||
        null}
      <div className="col-12 md:col-6">
        <Field
          name="timeOfDay"
          component={DropdownInput}
          label={label("schedule_time")}
          placeholder={label("schedule_time")}
          options={options.times}
          required
          validate={required}
        />
      </div>
      <div className="col-12 md:col-6">
        <Field
          name="timezone"
          filter={true}
          component={DropdownInput}
          label={label("timezone")}
          placeholder={label("timezone")}
          options={options.timezones}
          required
          validate={required}
        />
      </div>
      <div className="col-12 md:col-6">
        <Field
          name="format"
          component={DropdownInput}
          label={label("schedule_format")}
          placeholder={label("schedule_format")}
          options={options.formats}
          required
          validate={required}
        />
      </div>
    </div>
  );
};

const ScheduleDialog = ({
  visible,
  reportName = "",
  schedule = null,
  requesting = false,
  onHide,
  onSubmit,
  onRemove,
}) => {
  const intl = useIntl();
  const options = useScheduleOptions();
  // Built once per opening so the form does not reinitialise on every render.
  const initialValues = useMemo(
    () => (visible ? toFormValues(schedule, options) : {}),
    [visible, schedule, options]
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={intl.formatMessage({ id: "schedule_title" }, { name: reportName })}
      style={{ width: "40rem" }}
      breakpoints={{ "640px": "90vw" }}
    >
      {(visible && (
        <Form
          initialValues={initialValues}
          requesting={requesting}
          cancelBtn={{
            label: intl.formatMessage({ id: "cancel" }),
            onClick: onHide,
          }}
          submitBtn={{
            label: intl.formatMessage({ id: "save" }),
            onClick: (values) => onSubmit(toScheduleRequest(values)),
          }}
          extraBtn={
            schedule
              ? {
                  label: intl.formatMessage({ id: "schedule_remove" }),
                  onClick: () => onRemove(),
                }
              : null
          }
        >
          <ScheduleFields options={options} />
        </Form>
      )) ||
        null}
    </Dialog>
  );
};

ScheduleDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  reportName: PropTypes.string,
  schedule: PropTypes.object,
  requesting: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ScheduleDialog;
